import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import _get from 'lodash/get';
import { LazyQueryExecFunction } from '@apollo/client';
import classNames from 'classnames';
import Quill from 'quill';
import ReactQuill from 'react-quill-new';
// import 'react-quill-new/dist/quill.snow.css';
import { Mention, MentionBlot } from 'quill-mention';
import 'quill-paste-smart';
// import 'quill-mention/dist/quill.mention.css';
import { UsersSearchListQuery } from 'graphql-common';
import getFullName from '@lib/utils/getFullName';
import Collapse, { CollapseCropColor } from '@lib/components/Collapse/Collapse';
import createSearchFilters from '@lib/utils/createSearchFilters';
import withFloatingLabel from '@lib/hocs/withFloatingLabel';
import FileUploaderUppy, {
  AttachmentValue,
  UploaderView,
} from '../FileUploaderUppy/FileUploaderUppy';
import { EMPTY_EDITOR_HTML_VALUES, isTextEditorValueEmpty } from './utils';
import styles from './TextEditor.module.scss';
import './quill.show.scss';
import './quill.mention.scss';

const ImageFormat = Quill.import('formats/image');
// @ts-ignore
class CustomImage extends ImageFormat {
  static create(value) {
    const node = super.create();
    node.setAttribute('src', value?.src || '');
    if (value['data-image-id']) {
      node.setAttribute('data-image-id', value['data-image-id']);
    }
    return node;
  }

  static value(node) {
    return {
      src: node.getAttribute('src'),
      'data-image-id': node.getAttribute('data-image-id'),
    };
  }
}
// @ts-ignore
Quill.register(CustomImage, true);
Quill.register({ 'blots/mention': MentionBlot, 'modules/mention': Mention });

type ChangeEventType = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>;

type FetchUsersSearchListLazyQuery = LazyQueryExecFunction<
  UsersSearchListQuery,
  any
>;

type Props = {
  className?: string;
  'data-test-id'?: string;
  disabled?: boolean;
  fetchImageUrl?: (id: string) => Promise<string>;
  fetchUsersSearchListLazyQuery?: FetchUsersSearchListLazyQuery;
  hasError?: boolean;
  name?: string;
  onBlur?: () => void;
  onChange?: (event: ChangeEventType) => void;
  readOnly?: boolean;
  useCollapse?: boolean;
  cropColor?: CollapseCropColor;
  value: string;
};

const TextEditor = forwardRef(
  (props: Props, ref: React.ForwardedRef<HTMLInputElement>) => {
    const {
      className,
      'data-test-id': dataTestId,
      fetchImageUrl,
      fetchUsersSearchListLazyQuery,
      hasError,
      name,
      onBlur,
      onChange,
      readOnly,
      disabled,
      value,
      useCollapse = true,
      cropColor = CollapseCropColor.White,
    } = props;
    const quillRef = useRef<ReactQuill | null>(null);
    const [uploaderModalOpen, setUploaderModalOpen] = useState(false);
    const didHandleInitialEmptyChange = useRef(false);

    const onChangeHandle = (
      newValue: string,
      _delta?: any,
      _source?: string,
    ) => {
      let editorValue = newValue;
      if (EMPTY_EDITOR_HTML_VALUES.includes(newValue)) {
        editorValue = '';
      }

      // Ignore the initial Quill onChange on mount when content is effectively empty.
      // This prevents triggering external validation with an empty value on load.
      if (!didHandleInitialEmptyChange.current) {
        didHandleInitialEmptyChange.current = true;
        const initialPropEmpty = !value || value === '';
        if (initialPropEmpty && editorValue === '') {
          return;
        }
      }

      if (onChange && name) {
        const event = {
          target: {
            name,
            value: editorValue,
          },
        } as unknown as ChangeEventType;
        onChange(event);
      }
    };

    const handleImageUpload = () => {
      setUploaderModalOpen(true);
    };

    const handleImageUploadChange = (
      file: AttachmentValue | AttachmentValue[],
    ) => {
      const quillEditor = quillRef.current?.getEditor();
      const fileId = _get(file, 'file.id');
      if (quillEditor && !Array.isArray(file) && fileId) {
        const range = quillEditor.getSelection(true);
        quillEditor.insertEmbed(range.index, 'image', {
          src: 'loading-placeholder.png',
          'data-image-id': fileId,
        });
        setUploaderModalOpen(false);
      }
    };

    const handleEditorBlur = () => {
      if (!quillRef.current || !onChange || !name) return;
      const html = quillRef.current.getEditor().root.innerHTML;
      const cleaned = html.replace(
        /(\s+)(?=<\/(?:strong|em|i|u|p|li|span|h[1-3])>)/g,
        '',
      );
      const isEmpty = isTextEditorValueEmpty(cleaned);
      const event = {
        target: {
          name,
          value: isEmpty ? '' : cleaned,
        },
      } as unknown as ChangeEventType;
      onChange(event);
      if (onBlur) onBlur();
    };

    const mentionSource = useCallback(
      async (
        searchTerm: string,
        renderItem: (
          arg0: { id: number; value: string }[] | undefined,
          arg1: any,
        ) => void,
        mentionChar: string,
      ) => {
        if (fetchUsersSearchListLazyQuery) {
          try {
            const results = await fetchUsersSearchListLazyQuery({
              variables: {
                filters: [
                  ...createSearchFilters(searchTerm, [
                    { fieldName: 'email' },
                    { fieldName: 'fullName' },
                    { fieldName: 'personalId' },
                  ]),
                ],
              },
            });
            const collection = _get(results, 'data.data.collection', []).map(
              (user) => ({ id: user?.id, value: getFullName(user) }),
            );
            renderItem(collection, searchTerm);
          } catch (error) {
            console.error('Error fetching mention data:', error);
            renderItem([], searchTerm);
          }
        } else {
          renderItem([], searchTerm);
        }
      },
      [fetchUsersSearchListLazyQuery],
    );

    const modules = {
      toolbar: !readOnly
        ? [
            [{ header: [1, 2, 3, false] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['bold', 'italic', 'underline'],
            // [{ align: [] }],
            fetchImageUrl ? ['image'] : [],
            ['clean'],
          ]
        : false,
      clipboard: {
        matchVisual: false,
      },
      mention: {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['@'],
        source: mentionSource,
      },
    };

    useEffect(() => {
      if (quillRef.current && ref) {
        const editorRoot = quillRef.current.getEditor()
          .root as HTMLInputElement;
        if (typeof ref === 'function') {
          ref(editorRoot);
        } else if (ref && 'current' in ref) {
          // eslint-disable-next-line no-param-reassign
          ref.current = editorRoot;
        }
      }
    }, [ref]);

    useEffect(() => {
      if (quillRef.current && !readOnly) {
        const quillEditor = quillRef.current.getEditor();
        const toolbarModule = quillEditor.getModule('toolbar');
        if (toolbarModule) {
          // @ts-ignore
          toolbarModule?.addHandler('image', handleImageUpload);
        }
      }
    }, [readOnly]);

    useEffect(() => {
      const quillEditor = quillRef.current?.getEditor();
      if (quillEditor && fetchImageUrl) {
        const images = quillEditor.root.querySelectorAll('img[data-image-id]');
        images.forEach(async (img) => {
          const id = img.getAttribute('data-image-id');
          if (id) {
            const url = await fetchImageUrl(id);
            img.setAttribute('src', url);
          }
        });
      }
    }, [value, fetchImageUrl]);

    const readOnlyQuill = (
      <ReactQuill
        ref={quillRef}
        onChange={onChangeHandle}
        value={value}
        readOnly
        onBlur={handleEditorBlur}
        modules={modules}
      />
    );

    const renderReadOnlyContent = () => {
      if (useCollapse) {
        return (
          <Collapse
            minHeight={120}
            cropColor={cropColor}
            className={classNames({ [styles.disabledEditor]: disabled })}
          >
            {readOnlyQuill}
          </Collapse>
        );
      }
      return (
        <div
          className={classNames(styles.noScrollEditor, {
            [styles.disabledEditor]: disabled,
          })}
        >
          {readOnlyQuill}
        </div>
      );
    };

    return (
      <div
        className={classNames(className, styles.rootEditor)}
        data-test-id={dataTestId}
      >
        {readOnly ? (
          renderReadOnlyContent()
        ) : (
          <ReactQuill
            ref={quillRef}
            onChange={onChangeHandle}
            value={value}
            onBlur={handleEditorBlur}
            modules={modules}
            className={hasError ? 'hasError' : ''}
          />
        )}
        {uploaderModalOpen && (
          <FileUploaderUppy
            allowedFileTypes={['image/*']}
            type={UploaderView.modal}
            onChange={handleImageUploadChange}
            openButton={() => null}
            openUploaderModalOnInit
          />
        )}
      </div>
    );
  },
);

TextEditor.displayName = 'TextEditor';

export default memo(withFloatingLabel(TextEditor));
